/**
 * Cinematic Frame Navigation
 *
 * Smooth animated transitions between presentation frames.
 * The frame's size (xywh) determines the target zoom level.
 */

import type { FrameBlockModel } from '@blocksuite/affine-model';
import { Bound } from '@blocksuite/global/gfx';
import { ZOOM_MAX, ZOOM_MIN } from '@blocksuite/std/gfx';

/**
 * Animation style for frame transitions.
 * - 'cinematic': Full 3-phase zoom-out → pan → zoom-in (like workspace databases)
 * - 'smooth': Direct pan with zoom adjustment (no zoom-out effect)
 */
export type AnimationStyle = 'cinematic' | 'smooth';

// Easing function: ease-in-out-cubic for smooth acceleration/deceleration
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// Linear interpolation
function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * t;
}

// Clamp value between min and max
function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export interface ViewportLike {
  centerX: number;
  centerY: number;
  zoom: number;
  width: number;
  height: number;
  setCenter: (x: number, y: number) => void;
  setZoom: (zoom: number, focusPoint?: { x: number; y: number }) => void;
}

export interface ViewportNavigationTarget {
  centerX: number;
  centerY: number;
  zoom: number;
}

interface FrameNavigationOptions {
  /** Total animation duration in ms (default: 700 for smooth, 1000 for cinematic) */
  duration?: number;
  /** Minimum zoom level during cinematic flight (default: 0.75) */
  minZoom?: number;
  /** Navigator mode: 'fit' or 'fill' */
  mode?: 'fit' | 'fill';
  /** Animation style: 'smooth' (no zoom-out) or 'cinematic' (full effect) */
  style?: AnimationStyle;
}

/**
 * Calculate the target zoom level based on frame bounds.
 * Matches the calculation in viewport.setViewportByBound().
 * The frame's width/height determines the zoom - smaller frames = higher zoom.
 */
function calculateFrameZoom(
  viewport: ViewportLike,
  frameBound: Bound,
  mode: 'fit' | 'fill'
): number {
  const viewportWidth = viewport.width;
  const viewportHeight = viewport.height;

  let zoom: number;

  if (mode === 'fill') {
    // Fill mode: frame fills viewport (may crop edges)
    zoom = Math.max(
      viewportWidth / frameBound.w,
      viewportHeight / frameBound.h
    );
  } else {
    // Fit mode: entire frame visible (letterboxing if aspect ratios differ)
    zoom = Math.min(
      viewportWidth / frameBound.w,
      viewportHeight / frameBound.h
    );
  }

  // Clamp to valid zoom range (imported from viewport.ts)
  return clamp(zoom, ZOOM_MIN, ZOOM_MAX);
}

/**
 * Cinematically navigate to a presentation frame with smooth animation.
 *
 * Two animation styles:
 * - 'smooth': Direct pan with zoom adjustment (best for presentation mode)
 * - 'cinematic': Full 3-phase zoom-out → pan → zoom-in (like workspace databases)
 *
 * @param viewport - The GFX viewport instance
 * @param frame - The target frame model
 * @param options - Animation options
 */
export function cinematicNavigateToFrame(
  viewport: ViewportLike,
  frame: FrameBlockModel,
  options: FrameNavigationOptions = {}
): Promise<void> {
  const { mode = 'fit', style = 'smooth', minZoom = 0.75 } = options;

  // Default duration based on style
  const duration = options.duration ?? (style === 'cinematic' ? 1000 : 700);

  const frameBound = Bound.deserialize(frame.xywh);
  return cinematicNavigateToViewport(
    viewport,
    {
      centerX: frameBound.center[0],
      centerY: frameBound.center[1],
      zoom: calculateFrameZoom(viewport, frameBound, mode),
    },
    { duration, minZoom, style }
  );
}

/**
 * Cinematically restore a previously captured viewport.
 *
 * This is intentionally independent from a frame model so presentation mode
 * can return to the exact camera state that preceded the first frame.
 */
export function cinematicNavigateToViewport(
  viewport: ViewportLike,
  target: ViewportNavigationTarget,
  options: Pick<FrameNavigationOptions, 'duration' | 'minZoom' | 'style'> = {}
): Promise<void> {
  const { style = 'smooth', minZoom = 0.75 } = options;
  const duration = options.duration ?? (style === 'cinematic' ? 1000 : 700);

  return new Promise(resolve => {
    const targetX = target.centerX;
    const targetY = target.centerY;
    const targetZoom = clamp(target.zoom, ZOOM_MIN, ZOOM_MAX);
    const startX = viewport.centerX;
    const startY = viewport.centerY;
    const startZoom = viewport.zoom;

    // Calculate distance to determine animation behavior
    const distance = Math.sqrt(
      Math.pow(targetX - startX, 2) + Math.pow(targetY - startY, 2)
    );

    // For short distances or smooth style, use simpler animation
    const useSimpleAnimation = style === 'smooth' || distance < 500;

    // Calculate the zoom-out level for cinematic mode
    // Subtle zoom-out: only reduce to 85% of min zoom, capped at minZoom
    const zoomOutLevel = useSimpleAnimation
      ? Math.min(startZoom, targetZoom)
      : Math.max(minZoom, Math.min(startZoom, targetZoom) * 0.85);

    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const rawProgress = Math.min(elapsed / duration, 1);

      // Apply easing
      const progress = easeInOutCubic(rawProgress);

      let currentZoom: number;

      if (useSimpleAnimation) {
        // Smooth style: Direct zoom interpolation (no zoom-out effect)
        currentZoom = lerp(startZoom, targetZoom, progress);
      } else {
        // Cinematic style: 3-phase zoom (35% out, 30% pan, 35% in)
        if (progress < 0.35) {
          // Phase 1: Smooth zoom out
          const phaseProgress = progress / 0.35;
          currentZoom = lerp(
            startZoom,
            zoomOutLevel,
            easeInOutCubic(phaseProgress)
          );
        } else if (progress < 0.65) {
          // Phase 2: Pan at zoomed-out level
          currentZoom = zoomOutLevel;
        } else {
          // Phase 3: Smooth zoom in
          const phaseProgress = (progress - 0.65) / 0.35;
          currentZoom = lerp(
            zoomOutLevel,
            targetZoom,
            easeInOutCubic(phaseProgress)
          );
        }
      }

      // Pan position follows main progress curve
      const currentX = lerp(startX, targetX, progress);
      const currentY = lerp(startY, targetY, progress);

      // Apply to viewport
      viewport.setCenter(currentX, currentY);
      viewport.setZoom(currentZoom, { x: currentX, y: currentY });

      if (rawProgress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Ensure we land exactly on target
        viewport.setCenter(targetX, targetY);
        viewport.setZoom(targetZoom, { x: targetX, y: targetY });
        resolve();
      }
    };

    requestAnimationFrame(animate);
  });
}
